import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Flame, Phone, Mail, Calendar, Users, PartyPopper, ChevronRight, Coffee } from "lucide-react";

const NIVEAU_LABEL = { 1: "R1 · 1ère relance", 2: "R2 · relance ferme", 3: "R3 · dernière chance" };

export default function CoachBrief() {
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/coach/brief").then((r) => setBrief(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const relancer = async (pid, canal, send_email) => {
    setBusy(pid + canal);
    try {
      const { data } = await api.post(`/prospects/${pid}/relance`, { canal, send_email });
      if (data.email_sent) toast.success("Relance envoyée par email 📧 (mode test → boîte interne).", { duration: 5000 });
      else if (data.terminee) toast.success("R3 atteint — relance bouclée. Pense à conclure ou mettre en veille.", { duration: 5000 });
      else toast.success(`Relance notée 💪 Prochaine relance programmée pour le ${data.prochaine_date}.`, { duration: 5000 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible.");
    } finally {
      setBusy("");
    }
  };

  if (!brief) return null;
  const s = brief.stats;
  const chips = [
    { key: "a_relancer", label: "À relancer aujourd'hui", value: s.relances_du_jour, icon: Phone, tone: "#B8873A" },
    { key: "rdv_planifie", label: "RDV à venir", value: s.rdv_a_venir, icon: Calendar, tone: "#60A5FA" },
    { key: "chaud", label: "Chauds à réveiller", value: s.chauds_dormants, icon: Flame, tone: "#FB7185" },
    { key: "nouveau", label: "Nouveaux", value: s.nouveaux, icon: Users, tone: "#34D399" },
  ];

  return (
    <div className="mb-6 rounded-sm overflow-hidden border border-[#B8873A]/30 shadow-sm" data-testid="coach-brief">
      {/* Hero */}
      <div className="bg-[#111111] text-white p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#B8873A] font-bold mb-1">
              <Sparkles className="w-3.5 h-3.5" /> Le Coach · ton animateur du jour
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk" }} data-testid="coach-greeting">
              {brief.greeting}
            </h2>
            <p className="text-[#E7D9BF] text-sm mt-2 italic max-w-2xl" data-testid="coach-motivation">{brief.motivation}</p>
            <p className="text-slate-200 text-sm mt-2 max-w-2xl" data-testid="coach-focus">{brief.focus}</p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            {brief.streak > 0 && (
              <span className="flex items-center gap-1.5 bg-[#B8873A] text-[#111111] text-xs font-bold px-3 py-1.5 rounded-full" data-testid="coach-streak">
                <Flame className="w-3.5 h-3.5" /> {brief.streak} jour{brief.streak > 1 ? "s" : ""} d'affilée
              </span>
            )}
            {s.signatures_semaine > 0 && (
              <span className="flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-xs font-semibold px-3 py-1.5 rounded-full" data-testid="coach-celebration">
                <PartyPopper className="w-3.5 h-3.5" /> {s.signatures_semaine} signature{s.signatures_semaine > 1 ? "s" : ""} cette semaine 🎉
              </span>
            )}
          </div>
        </div>

        {/* Stats chips */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          {chips.map((c) => (
            <button
              key={c.key}
              onClick={() => navigate(`/prospects?statut=${c.key}`)}
              data-testid={`coach-chip-${c.key}`}
              className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-sm px-3 py-2.5 transition-colors text-left"
            >
              <c.icon className="w-4 h-4 shrink-0" style={{ color: c.tone }} />
              <div className="min-w-0">
                <div className="text-xl font-bold leading-none" style={{ fontFamily: "Cabinet Grotesk" }}>{c.value}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 truncate">{c.label}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Action list */}
      <div className="bg-[#FBFAF7] p-5">
        <div className="flex items-center gap-2 mb-3">
          <Coffee className="w-4 h-4 text-[#B8873A]" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700">À relancer aujourd'hui</h3>
          <span className="ml-auto text-xs text-slate-400">{s.relances_du_jour} au total</span>
        </div>
        {brief.actions.length === 0 ? (
          <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-sm px-4 py-6 text-center" data-testid="coach-empty">
            Rien à relancer pour l'instant — tu es à jour. Beau travail&nbsp;! 👏
          </div>
        ) : (
          <div className="space-y-2">
            {brief.actions.map((a) => (
              <div key={a.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-sm px-4 py-2.5 hover:border-[#B8873A]/50 transition-colors" data-testid={`coach-action-${a.id}`}>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#B8873A]/10 text-[#B8873A] border border-[#B8873A]/30 shrink-0">
                  {NIVEAU_LABEL[a.niveau] || "R1"}
                </span>
                <button onClick={() => navigate(`/prospects/${a.id}`)} className="flex-1 min-w-0 text-left group">
                  <div className="text-sm font-semibold text-[#111111] truncate group-hover:text-[#B8873A]">
                    {a.prenom} {a.nom}
                    <ChevronRight className="w-3.5 h-3.5 inline-block ml-0.5 -mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="text-xs text-slate-500 truncate">{a.programme || "Projet non précisé"}{a.telephone ? ` · ${a.telephone}` : ""}</div>
                </button>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => relancer(a.id, "appel", false)}
                    disabled={busy === a.id + "appel"}
                    data-testid={`coach-relance-appel-${a.id}`}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <Phone className="w-3.5 h-3.5" /> Appelé
                  </button>
                  <button
                    onClick={() => relancer(a.id, "email", true)}
                    disabled={!a.has_email || busy === a.id + "email"}
                    title={a.has_email ? "" : "Pas d'email renseigné"}
                    data-testid={`coach-relance-email-${a.id}`}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-sm bg-[#111111] text-white hover:bg-slate-800 disabled:opacity-40"
                  >
                    <Mail className="w-3.5 h-3.5" /> {busy === a.id + "email" ? "…" : `Email ${"R" + a.niveau}`}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
